import React, { useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import './ConfirmDialog.scss'

interface ConfirmDialogProps {
  show: boolean
  title: string
  message: string
  messageBold?: string
  confirmText?: string
  cancelText?: string
  onConfirm: (permanentDelete?: boolean) => void
  onCancel: () => void
  variant?: 'danger' | 'warning' | 'primary'
  hideCancelButton?: boolean
  showPermanentDeleteOption?: boolean
  permanentDeleteLabel?: string
  permanentDeleteDescription?: string
  children?: React.ReactNode
  isConfirming?: boolean
  disableConfirm?: boolean
  disableCancel?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  show,
  title,
  message,
  messageBold,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = 'danger',
  hideCancelButton = false,
  showPermanentDeleteOption = false,
  permanentDeleteLabel,
  permanentDeleteDescription,
  children,
  isConfirming = false,
  disableConfirm = false,
  disableCancel = false,
}) => {
  const { t } = useTranslation()
  const [permanentDelete, setPermanentDelete] = useState(false)

  const handleConfirm = () => {
    if (isConfirming || disableConfirm) {
      return
    }
    onConfirm(showPermanentDeleteOption ? permanentDelete : undefined)
  }

  const handleCancel = () => {
    if (isConfirming || disableCancel) {
      return
    }
    setPermanentDelete(false)
    onCancel()
  }

  const getConfirmButtonText = () => {
    if (confirmText) {
      return confirmText
    }
    if (showPermanentDeleteOption) {
      return permanentDelete ? t('permanent_delete') : t('move_to_trash')
    }
    return t('proceed')
  }

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      onExited={() => setPermanentDelete(false)}
      centered
      className="confirm-dialog"
      backdrop={isConfirming ? 'static' : true}
      keyboard={!isConfirming}
    >
      <Modal.Header closeButton={!isConfirming && !disableCancel}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {messageBold && <p className="fw-bold mb-2">{messageBold}</p>}
        <p className="mb-0">{message}</p>
        {children}
        {showPermanentDeleteOption && (
          <div className="mt-3">
            <Form.Check
              type="checkbox"
              id="permanentDeleteCheck"
              checked={permanentDelete}
              onChange={(e) => setPermanentDelete(e.target.checked)}
              label={permanentDeleteLabel || t('permanent_delete')}
            />
            {permanentDeleteDescription && <small className="text-muted d-block mt-1 ms-4">{permanentDeleteDescription}</small>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!hideCancelButton && (
          <Button variant="secondary" onClick={handleCancel} disabled={isConfirming || disableCancel}>
            {cancelText || t('cancel')}
          </Button>
        )}
        <Button variant={variant} onClick={handleConfirm} disabled={isConfirming || disableConfirm}>
          {isConfirming && <Spinner animation="border" size="sm" className="me-2" />}
          {getConfirmButtonText()}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConfirmDialog
